import React, { useState, useCallback, useEffect } from 'react';
import { PrintOptions } from '../../lib/print/printCalendarService';
import Button from '../common/Button';

interface PrintModalProps {
    isOpen: boolean;
    onClose: () => void;
    onPrint: (options: PrintOptions) => void;
    employeeCount: number;
}

const PrintModal: React.FC<PrintModalProps> = ({ isOpen, onClose, onPrint, employeeCount }) => {
    const [mode, setMode] = useState<'compact' | 'detailed'>('compact');
    const [showExactHours, setShowExactHours] = useState(false);
    const [showLegend, setShowLegend] = useState(true);
    const [showDepartment, setShowDepartment] = useState(false);
    const [showTotal, setShowTotal] = useState(true);

    // Reset defaults when modal opens
    useEffect(() => {
        if (isOpen) {
            // Use compact mode by default if more than 15 employees
            setMode(employeeCount > 15 ? 'compact' : 'detailed');
            setShowExactHours(false);
            setShowLegend(true);
            setShowDepartment(false);
            setShowTotal(true);
        }
    }, [isOpen, employeeCount]);

    const handlePrint = useCallback(() => {
        const options: PrintOptions = {
            mode,
            showExactHours,
            showLegend,
            showDepartment,
            showTotal
        };

        onPrint(options);
        onClose();
    }, [mode, showExactHours, showLegend, showDepartment, showTotal, onPrint, onClose]);

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Imprimir Calendario Semanal
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Employee count info */}
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span><strong>{employeeCount}</strong> empleados en el calendario</span>
                    </div>
                </div>

                {/* Mode selection */}
                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Modo de impresión
                    </label>
                    <div className="space-y-2">
                        <label className="flex items-start gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <input
                                type="radio"
                                name="printMode"
                                value="compact"
                                checked={mode === 'compact'}
                                onChange={(e) => setMode(e.target.value as 'compact' | 'detailed')}
                                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    Compacto (A4 apaisado)
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                    Hasta 30 empleados por hoja • 7pt fuente • Códigos cortos
                                </div>
                            </div>
                        </label>

                        <label className="flex items-start gap-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                            <input
                                type="radio"
                                name="printMode"
                                value="detailed"
                                checked={mode === 'detailed'}
                                onChange={(e) => setMode(e.target.value as 'compact' | 'detailed')}
                                className="mt-1 w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                            />
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">
                                    Detallado (A4 apaisado)
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400">
                                    Hasta 15 empleados por hoja • 9pt fuente • Más legible
                                </div>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Options */}
                <div className="mb-6 space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showExactHours}
                            onChange={(e) => setShowExactHours(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Mostrar horas exactas (ej: 08:00-14:00)
                        </span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showLegend}
                            onChange={(e) => setShowLegend(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Mostrar leyenda de códigos al pie
                        </span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showDepartment}
                            onChange={(e) => setShowDepartment(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Mostrar columna de departamento
                        </span>
                    </label>

                    <label className="flex items-center gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showTotal}
                            onChange={(e) => setShowTotal(e.target.checked)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                            Mostrar columna total de horas
                        </span>
                    </label>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handlePrint}
                        icon={<span className="text-lg">🖨️</span>}
                    >
                        Imprimir
                    </Button>
                </div>

                {/* Print tips */}
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                        💡 Consejos de impresión
                    </h3>
                    <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                        <li>• Usa la opción "Guardar como PDF" en el diálogo de impresión</li>
                        <li>• Asegúrate de seleccionar orientación horizontal (apaisada)</li>
                        <li>• Deshabilita "Encabezados y pies de página" para mejor ajuste</li>
                        <li>• En Chrome: Configuración → Más configuraciones → Encabezados y pies</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default PrintModal;
